import React, { startTransition, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { Route, Routes, Navigate, useLocation, matchPath } from 'react-router-dom';
import 'assets/css/plugin-styles.css';
import 'react-quill/dist/quill.snow.css'; // ES6
// Chakra imports
import { Portal, Box, useDisclosure, useColorModeValue, Spinner } from '@chakra-ui/react';
// Custom components
import MainPanel from 'components/Layout/MainPanel';
import PanelContainer from 'components/Layout/PanelContainer';
import PanelContent from 'components/Layout/PanelContent';
// Layout components
import AdminNavbar from 'components/Navbars/AdminNavbar.js';
import { SidebarContext } from 'contexts/SidebarContext';
import { ROUTES } from 'constants/routes-path';
import { appRoutes } from 'routes.js';
import { hasPermission } from 'utils/permission';
import { PROFILES } from 'constants/permission';
import RouteGuard from 'guards/AuthGuard';
import { AuthContext } from 'contexts/AuthContext';
import { CUSTOMER_LOGIN_IMAGE } from 'constants/config';
import CTBChat from "../components/Chat/CTBChat";
import ChangePasswordPage from 'views/Pages/Users/ChangePasswordPage';

export const GOTTPanelConfig = {
  name: 'GOTT - Gestor de Operações de Trânsito e Transporte',
  path: ROUTES.panel,
  layout: ROUTES.panel,
  matcher: `${ROUTES.panel}/*`,
  dir: 'ltr'
}

const BACKGROUND_URL = CUSTOMER_LOGIN_IMAGE ?? "/assets/headers/cg_header.webp"

const GOTTPanel = (props) => {
  const { ...rest } = props;
  const [fixed, setFixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(275);
  const [breadcrumbKeyParams, setBreadcrumbKeyParams] = useState({});
  const { onOpen } = useDisclosure();
  const location = useLocation();
  const { getUserData } = useContext(AuthContext);
  const user = getUserData();

  const routeStack = useMemo(() => getActiveRouteStack(appRoutes, location.pathname, location.search), [appRoutes, location]);
  const routeElements = useMemo(() => getRoutes(appRoutes), [appRoutes]);

  const bgBoxHeight = '40vh';

  const isRoutingEnabled = location.pathname !== '/admin/full-screen-maps';
  const isSecondaryNavbar = routeStack.some(isActiveRoute);
  const activeRoute = routeStack.find(isActiveRoute);
  const activeRouteName = activeRoute?.pageTitle ?? activeRoute?.name ?? GOTTPanelConfig.name;

  const allotmentsPath = ROUTES.transportManagement + ROUTES.violationAllotments;
  const settingsPath = ROUTES.transportManagement + ROUTES.settings;
  const settingsRedirect = ROUTES.panel + ROUTES.management + ROUTES.parameters;
  const transportRedirect = ROUTES.transportManagementPath + ROUTES.trafficViolations;

  const [bgLoaded, setBgLoaded] = useState(false);

  document.documentElement.dir = GOTTPanelConfig.dir;
  document.documentElement.layout = GOTTPanelConfig.layout;

  useEffect(() => {
    const img = new Image();
    img.src = BACKGROUND_URL;
    img.onload = () => setBgLoaded(true);
  }, []);

  const updateBreadcrumbKeyParams = (params) => {
    startTransition(() => {
      setBreadcrumbKeyParams(params);
    });
  };

  function isActiveRoute(route) {
    return matchPath((route.layout ?? '') + route.path, location.pathname) != null;
  }

  function getActiveRouteStack(routes = [], locationPathname = location.pathname, locationSearch = location.search) {
    const searchParams = new URLSearchParams(locationSearch);

    for (const route of routes) {
      const fullPath = (route.layout ?? '') + route.path;

      const pathMatches = matchPath({ path: fullPath, end: false }, locationPathname);
      const queryParamsMatch = !route.queryParams || Object.entries(route.queryParams).every(
        ([key, value]) => searchParams.get(key) === value
      );

      if (pathMatches && queryParamsMatch) {
        const currentRoute = { ...route };
        const dynamicQueryParams = {};
        for (const [key, value] of searchParams.entries()) {
          dynamicQueryParams[key] = value;
        }
        currentRoute.queryParams = { ...currentRoute.queryParams, ...dynamicQueryParams };


        if (route.items?.length > 0) {
          const childStack = getActiveRouteStack(route.items, locationPathname, locationSearch);
          if (childStack.length > 0) {
            return [currentRoute, ...childStack];
          }
        }
        return [currentRoute];
      }

      if (route.items?.length > 0) {
        const childStack = getActiveRouteStack(route.items, locationPathname, locationSearch);
        if (childStack.length > 0) {
          return [route, ...childStack];
        }
      }
    }

    return [];
  }

  function getRoutes(routes = []) {
    return routes.map((route, key) => {
      if (route.permissions && !hasPermission({ userPermissions: user?.profilesPermissions, requiredPermissions: route.permissions })) {
        return null
      }
      if ((route.collapse || route.category) && route.ignoreRoute !== false) {
        return getRoutes(route.items);
      }

      // Check layout compatibility last to avoid routing issues
      if (route.layout === GOTTPanelConfig.layout) {
        if (route.items?.length > 0) {
          return (
            <Route key={Math.floor(Math.random() * 2048)+key} element={<RouteGuard allowedRoles={[PROFILES.adm, PROFILES.agent]} permissions={route.permissions} />}>
              <>
                <Route key={key} path={route.path} element={<Suspense>{route.component}</Suspense>} />
                {getRoutes(route.items)}
              </>
            </Route>
          );
        }
        return (
          <Route key={Math.floor(Math.random() * 1024)+key} element={<RouteGuard allowedRoles={[PROFILES.adm, PROFILES.agent]} permissions={route.permissions} />}>
            <Route key={key} path={route.path} element={<Suspense>{route.component}</Suspense>} />
          </Route>
        )
      }

      return null;
    });
  }

  return (
    <Box>
      <SidebarContext.Provider value={{
        sidebarWidth,
        setSidebarWidth,
        toggleSidebar,
        setToggleSidebar,
        breadcrumbKeyParams,
        updateBreadcrumbKeyParams,
        routeStack,
        displayDocs: false
      }}>
        <Box 
          minH={bgBoxHeight} 
          h="100% !important" 
          w="100%" 
          position="absolute" 
          top="0" 
          zIndex={-1} 
          overflow="hidden"
          isolation="isolate"
        >
          <Box
            position="absolute"
            inset="0"
            backgroundImage={bgLoaded ? `url(${BACKGROUND_URL})` : 'none'}
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            transition={bgLoaded ? 'all 0.3s ease-in-out' : 'none'}
            filter="blur(5px)"
            transform="scale(1)"
            animation={bgLoaded ? 'zoomLoop 30s ease-in-out infinite' : 'none'}
            sx={{
              '@keyframes zoomLoop': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
                '100%': { transform: 'scale(1)' },
              }
            }}
          />
          <Box
            position="absolute"
            inset="0"
            bg="rgba(0, 0, 0, 0.6)"
            zIndex={1}
          />
        </Box>
        <MainPanel w="100%" position="relative" zIndex={0}>
          <Portal>
            <Box>
              <Suspense fallback={<Spinner size="xl" />}>
                <AdminNavbar
                  fixed={fixed}
                  brandText={activeRouteName}
                  secondary={isSecondaryNavbar}
                  logotext={GOTTPanel.name}
                  {...rest} />
              </Suspense>
            </Box>
          </Portal>

          {isRoutingEnabled && (
            <PanelContent>
              <CTBChat loggedUserName={user.pessoa.nome} />
              <PanelContainer pt={{ sm:"30px", lg:"90px" }} pb={{ sm:"110px", lg:"30px" }}>
                <Routes>
                  <Route path='/' element={<Navigate to={transportRedirect} replace />} />

                  <Route path={ROUTES.transportManagement} element={<Navigate to={transportRedirect} replace />} />

                  <Route path={ROUTES.management} element={<Navigate to={settingsRedirect} replace />} />
                  <Route path={ROUTES.passwordChange} element={<ChangePasswordPage />} />
                  {routeElements}
                </Routes>
              </PanelContainer>
            </PanelContent>
          )}
        </MainPanel>
      </SidebarContext.Provider>
    </Box>
  );
}

export default GOTTPanel;
